import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import { ArrowLeft, Save } from 'lucide-react';
import CustomSelect from '../../components/ui/CustomSelect';
import { useToast } from '../../context/ToastContext';

const DELIVERY_METHOD_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
  { value: 'hybrid', label: 'Hybrid' },
];

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Direncanakan' },
  { value: 'ongoing', label: 'Berlangsung' },
  { value: 'completed', label: 'Selesai' },
  { value: 'cancelled', label: 'Dibatalkan' },
];

const INITIAL_FORM_DATA = {
  year: new Date().getFullYear(),
  program_name: '',
  target_date: '',
  objective: '',
  budget: '',
  instructor: '',
  organizer: '',
  delivery_method: 'offline',
  participants: [],
  competency_gaps: [],
  status: 'planned',
  evaluation: '',
};

export default function EmployeeDevelopmentForm() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const item = await apiClient.get(`/hris/development/${id}`);
      if (item) {
        setFormData({
          ...INITIAL_FORM_DATA,
          ...item,
          budget: item.budget?.toString() || '',
        });
      }
    } catch (error) {
      // Error loading data
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) loadData();
  }, [isEdit, loadData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Format currency for display (without Rp prefix for input)
  const formatCurrencyInput = value => {
    if (!value && value !== 0) return '';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d]/g, '')) : value;
    if (isNaN(numValue) || numValue === 0) return '';
    return new Intl.NumberFormat('id-ID').format(numValue);
  };

  // Handle currency input change
  const handleCurrencyChange = (field, value) => {
    // Remove all non-digit characters
    const cleaned = value.replace(/[^\d]/g, '');
    if (cleaned === '') {
      handleChange(field, '');
      return;
    }
    const numValue = parseFloat(cleaned);
    if (!isNaN(numValue)) {
      handleChange(field, numValue.toString());
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        budget: parseFloat(formData.budget) || 0,
      };

      if (isEdit) {
        await apiClient.put(`/hris/development/${id}`, dataToSave);
        showSuccess('Program pengembangan berhasil diperbarui');
      } else {
        await apiClient.post('/hris/development', dataToSave);
        showSuccess('Program pengembangan berhasil ditambahkan');
      }

      setTimeout(() => {
        navigate('/employeedevelopmentlist');
      }, 1000);
    } catch (error: any) {
      showError(error?.message || 'Gagal menyimpan program pengembangan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-300"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit' : 'Tambah'} Program Pengembangan
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold">Informasi Program</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tahun <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.year}
                onChange={e => handleChange('year', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nama Program <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.program_name}
                onChange={e => handleChange('program_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Waktu <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.target_date}
                onChange={e => handleChange('target_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  Rp
                </span>
                <input
                  type="text"
                  value={formatCurrencyInput(formData.budget || '')}
                  onChange={e => handleCurrencyChange('budget', e.target.value)}
                  className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pengajar</label>
              <input
                type="text"
                value={formData.instructor}
                onChange={e => handleChange('instructor', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Penyelenggara</label>
              <input
                type="text"
                value={formData.organizer}
                onChange={e => handleChange('organizer', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Metode Pelaksanaan
              </label>
              <CustomSelect
                value={formData.delivery_method}
                onChange={v => handleChange('delivery_method', v)}
                placeholder="Pilih metode"
                width="w-full"
                options={DELIVERY_METHOD_OPTIONS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <CustomSelect
                value={formData.status}
                onChange={v => handleChange('status', v)}
                placeholder="Pilih status"
                width="w-full"
                options={STATUS_OPTIONS}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tujuan</label>
            <textarea
              value={formData.objective}
              onChange={e => handleChange('objective', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Evaluasi</label>
            <textarea
              value={formData.evaluation}
              onChange={e => handleChange('evaluation', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 border rounded-lg hover:bg-gray-100 hover:border-gray-400 transition-all duration-300"
          >
            Batal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 active:scale-95 flex items-center gap-2 disabled:opacity-50 transition-all duration-300"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>
      </form>
    </div>
  );
}
